import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import FournisseurDocumentFormPage, { buildConversionProduitsQte, docTypeLabel } from '../fournisseurs/FournisseurDocumentFormPage';
import './ProduitsManagement.css';
import '../fournisseurs/FournisseurDocumentFormPage.css';

const API_URL = import.meta.env.VITE_API_URL;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const BonCommandeCreate = ({ onBack, onNavigate, currentPage, onLogout, produits = [], types = [], fournisseurs = [], prefill = null }) => {
  const [viewMode, setViewMode] = useState('list');
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [filterFourn, setFilterFourn] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterTypeDoc, setFilterTypeDoc] = useState('');
  const [formContext, setFormContext] = useState(null);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailObject, setEmailObject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailDocId, setEmailDocId] = useState(null);
  const [emailDocNumero, setEmailDocNumero] = useState('');

  const fetchDocuments = async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API_URL}/documents-financiers/fournisseurs/all`, { headers: authHeader() });
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Erreur chargement documents fournisseurs:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'list') fetchDocuments();
  }, [viewMode]);

  const filteredDocs = useMemo(() => {
    return documents.filter((doc) => {
      const matchFournisseur = !filterFourn || doc.fournisseur?._id === filterFourn;
      const matchType = !filterTypeDoc || doc.type === filterTypeDoc;
      const matchCategorie = !filterCat || fournisseurs
        .find(f => f._id === doc.fournisseur?._id)
        ?.typeProduit
        ?.some(tp => (tp._id || tp) === filterCat);

      return matchFournisseur && matchType && matchCategorie;
    });
  }, [documents, filterFourn, filterCat, filterTypeDoc, fournisseurs]);

  const openCreateForm = () => {
    setFormContext({
      initialType: 'bon_commande',
      initialDoc: null,
      initialProduitsQte: {},
      sourceBonCommandeId: null
    });
    setViewMode('form');
  };

  useEffect(() => {
    if (!prefill?.fournisseurId && !prefill?.produitId) return;
    setFormContext({
      initialType: 'bon_commande',
      initialDoc: null,
      initialProduitsQte: {},
      sourceBonCommandeId: null,
      initialFournisseurId: prefill.fournisseurId || '',
      initialProduitId: prefill.produitId || ''
    });
    setViewMode('form');
  }, [prefill?.fournisseurId, prefill?.produitId]);

  const handleConvertToBonLivraison = (doc) => {
    setFormContext({
      initialType: 'bon_livraison',
      initialDoc: doc,
      initialProduitsQte: buildConversionProduitsQte(doc, produits),
      sourceBonCommandeId: doc._id
    });
    setViewMode('form');
    Swal.fire({
      title: 'Formulaire pre-rempli',
      text: `Vous creez un bon de livraison a partir de ${doc.numero}.`,
      icon: 'info',
      timer: 2500,
      toast: true,
      position: 'top-end',
      showConfirmButton: false
    });
  };

  const handleDuplicateDocument = (doc) => {
    setFormContext({
      initialType: doc.type === 'bon_livraison' ? 'bon_commande' : doc.type,
      initialDoc: doc,
      initialProduitsQte: buildConversionProduitsQte(doc, produits),
      sourceBonCommandeId: null
    });
    setViewMode('form');
    Swal.fire({
      title: 'Formulaire pre-rempli',
      text: `Vous dupliquez le document ${doc.numero}.`,
      icon: 'info',
      timer: 2500,
      toast: true,
      position: 'top-end',
      showConfirmButton: false
    });
  };

  const handleDeleteDocument = async (id) => {
    const result = await Swal.fire({
      title: 'Etes-vous sur ?',
      text: "Ce document sera marque comme supprime.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_URL}/documents-financiers/${id}`, { headers: authHeader() });
      Swal.fire('Supprime', 'Le document a ete supprime avec succes.', 'success');
      fetchDocuments();
    } catch (error) {
      Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleValidateDocument = async (doc) => {
    const result = await Swal.fire({
      title: 'Valider ce document ?',
      text: 'Cette action est irreversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, valider',
      cancelButtonText: 'Annuler'
    });

    if (!result.isConfirmed) return;
    try {
      await axios.patch(`${API_URL}/documents-financiers/${doc._id}/valider`, {}, { headers: authHeader() });
      Swal.fire('Valide', 'Le document a ete valide.', 'success');
      fetchDocuments();
    } catch (error) {
      Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de la validation', 'error');
    }
  };

  const openEmailModal = (doc) => {
    setEmailDocId(doc._id);
    setEmailDocNumero(doc.numero);
    setEmailTo(doc.fournisseur?.email || '');
    setEmailObject(`Nouveau ${docTypeLabel(doc.type)} - ${doc.numero}`);
    setEmailBody(`Bonjour,\n\nVeuillez trouver ci-joint notre ${docTypeLabel(doc.type).toLowerCase()} numero ${doc.numero}.\n\nCordialement,\nL equipe IB-SOL-ENERGY.`);
    setShowEmailModal(true);
  };

  const handleSendEmail = async (event) => {
    event.preventDefault();
    setEmailSending(true);
    try {
      await axios.post(`${API_URL}/documents-financiers/${emailDocId}/envoyer-email`, {
        destinataire: emailTo,
        sujet: emailObject,
        message: emailBody
      }, { headers: authHeader() });
      Swal.fire('Succes', 'Email envoye avec succes', 'success');
      setShowEmailModal(false);
    } catch (error) {
      Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de l envoi email', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  const generateWhatsAppLink = (doc) => {
    const cleanPhone = (doc.fournisseur?.telephone || '').replace(/\D/g, '');
    const messageText = `Bonjour,\nVeuillez trouver ci-joint notre ${docTypeLabel(doc.type).toLowerCase()} numero ${doc.numero}.`;
    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(messageText)}`;
  };

  if (viewMode === 'form') {
    return (
      <div className="pm-layout">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <div className="pm-content">
          <FournisseurDocumentFormPage
            fournisseurs={fournisseurs}
            produits={produits}
            types={types}
            initialType={formContext?.initialType}
            initialDoc={formContext?.initialDoc}
            initialProduitsQte={formContext?.initialProduitsQte}
            initialFournisseurId={formContext?.initialFournisseurId}
            initialProduitId={formContext?.initialProduitId}
            sourceBonCommandeId={formContext?.sourceBonCommandeId}
            contextLabel="Gestion produits"
            onBack={() => {
              setFormContext(null);
              setViewMode('list');
            }}
            onCreated={async () => {
              setFormContext(null);
              setViewMode('list');
              await fetchDocuments();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pm-layout">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="pm-content" style={{ padding: '2rem' }}>
        <div className="pm-header" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '20px', background: 'white', padding: '1rem 1.5rem', borderRadius: '12px', boxShadow: '0 4px 10px -5px rgba(0,0,0,0.05)' }}>
          <button className="pm-btn-cancel" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.6rem 1rem', background: '#f8fafc', color: '#1e3a5f', border: '1px solid #e2e8f0', cursor: 'pointer', borderRadius: '8px', fontWeight: 'bold' }}>
            <i className="fas fa-arrow-left"></i> Retour
          </button>
          <div className="pm-header-text" style={{ flex: 1 }}>
            <h1 style={{ color: '#1e3a5f', margin: 0, fontSize: '1.4rem' }}>
              <i className="fas fa-file-invoice" style={{ marginRight: '10px', color: '#f59e0b' }}></i>
              Bons de Commande / Livraison
            </h1>
            <p style={{ margin: '5px 0 0', color: '#64748b' }}>Documents fournisseurs centralises avec le formulaire BC/BL partage.</p>
          </div>
          <button className="pm-btn-add" style={{ padding: '0.8rem 1.2rem', fontWeight: 'bold', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={openCreateForm}>
            <i className="fas fa-plus"></i> Nouveau BC
          </button>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.05)' }}>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', background: '#f8fafc', padding: '15px', borderRadius: '10px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
            <select className="pm-filter-select" value={filterTypeDoc} onChange={(event) => setFilterTypeDoc(event.target.value)} style={{ flex: 1, minWidth: '200px' }}>
              <option value="">Tous les types de documents</option>
              <option value="bon_commande">Bons de Commande</option>
              <option value="bon_livraison">Bons de Livraison</option>
            </select>
            <select className="pm-filter-select" value={filterCat} onChange={(event) => setFilterCat(event.target.value)} style={{ flex: 1, minWidth: '200px' }}>
              <option value="">Toutes les categories</option>
              {types.map(t => <option key={t._id} value={t._id}>{t.label}</option>)}
            </select>
            <select className="pm-filter-select" value={filterFourn} onChange={(event) => setFilterFourn(event.target.value)} style={{ flex: 1, minWidth: '200px' }}>
              <option value="">Tous les fournisseurs</option>
              {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.raisonSociale}</option>)}
            </select>
          </div>

          {loadingDocs ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#3b82f6' }}></i>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
              <i className="fas fa-inbox fa-3x" style={{ marginBottom: '15px', color: '#cbd5e1' }}></i>
              <p>Aucun document trouve.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredDocs.map(doc => (
                <div key={doc._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', flexWrap: 'wrap', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '6px 14px', background: '#1e3a5f', color: 'white', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      {docTypeLabel(doc.type)}
                    </span>
                    <strong style={{ fontSize: '1.1rem', color: '#1e293b' }}>{doc.numero}</strong>
                    <span style={{ color: '#64748b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <i className="far fa-calendar-alt"></i> {new Date(doc.dateCreation).toLocaleDateString('fr-FR')}
                    </span>
                    <span style={{ padding: '4px 10px', background: doc.statut === 'valide' ? '#dcfce7' : '#fef9c3', color: doc.statut === 'valide' ? '#166534' : '#a16207', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      {doc.statut}
                    </span>
                    <span style={{ fontSize: '0.95rem', color: '#475569', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '10px' }}>
                      <i className="fas fa-building" style={{ color: '#94a3b8' }}></i>
                      {doc.fournisseur?.raisonSociale}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <a href={`${API_URL}/documents-financiers/${doc._id}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', background: '#dc2626', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="fas fa-file-pdf"></i> PDF
                    </a>
                    <button onClick={() => openEmailModal(doc)} style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="fas fa-envelope"></i> Email
                    </button>
                    <a href={generateWhatsAppLink(doc)} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="fab fa-whatsapp"></i> WhatsApp
                    </a>
                    {doc.statut !== 'valide' && (
                      <button onClick={() => handleValidateDocument(doc)} style={{ padding: '8px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-check"></i> Valider
                      </button>
                    )}
                    {doc.type === 'bon_commande' && doc.statut === 'valide' && (
                      <button onClick={() => handleConvertToBonLivraison(doc)} style={{ padding: '8px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-truck"></i> Convertir en BL
                      </button>
                    )}
                    {doc.type === 'bon_commande' && (
                      <button onClick={() => handleDuplicateDocument(doc)} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-copy"></i> Dupliquer
                      </button>
                    )}
                    <button onClick={() => handleDeleteDocument(doc._id)} style={{ padding: '8px 16px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }} title="Supprimer">
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showEmailModal && (
          <div className="pm-bc-modal-overlay" onClick={() => setShowEmailModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="pm-bc-modal-content" onClick={event => event.stopPropagation()} style={{ background: 'white', padding: '25px', borderRadius: '12px', width: '80%', maxWidth: '600px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
                <h2 style={{ color: '#1e3a5f', margin: 0 }}><i className="fas fa-envelope"></i> Envoyer par Email</h2>
                <button onClick={() => setShowEmailModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
              </div>
              <form onSubmit={handleSendEmail}>
                <div className="pm-form-group" style={{ marginBottom: '15px' }}>
                  <label>Destinataire *</label>
                  <input type="email" value={emailTo} onChange={event => setEmailTo(event.target.value)} required placeholder="email@fournisseur.com" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px' }} />
                </div>
                <div className="pm-form-group" style={{ marginBottom: '15px' }}>
                  <label>Objet *</label>
                  <input type="text" value={emailObject} onChange={event => setEmailObject(event.target.value)} required style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px' }} />
                </div>
                <div className="pm-form-group" style={{ marginBottom: '15px' }}>
                  <label>Message</label>
                  <textarea value={emailBody} onChange={event => setEmailBody(event.target.value)} rows="6" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px' }} />
                </div>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', color: '#475569', fontSize: '0.9rem' }}>
                  <i className="fas fa-paperclip"></i> Le PDF du document <strong>{emailDocNumero}</strong> sera joint automatiquement.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowEmailModal(false)} className="pm-btn-cancel" style={{ padding: '10px 20px' }}>Annuler</button>
                  <button type="submit" disabled={emailSending} className="pm-btn-submit" style={{ padding: '10px 20px', borderRadius: '8px' }}>
                    {emailSending ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BonCommandeCreate;
